import React, { useEffect, useState } from "react";

import styled, { css } from "styled-components";
import PlaceholderImg from "./assets/icon_placeholder.svg";
import { Button, Text } from "../../../../components";
import { InitLivenessModal } from "./InitLiveness.modal";
import { CameraIFrameModal, QRIFrameModal } from "./IFrame.modal";
import { ConfirmCloseModal } from "./ConfirmClose.modal";
import { ResultData, ResultModal } from "./Result.modal";
import { observer } from "mobx-react-lite";
import {
  useInitOneKycProcess,
  useOneKycAttemptCounter,
  useOneKycResult,
  useResultTranslation,
} from "./hooks";
import {
  LivenessErrorCode,
  LivenessResult,
  LIvenessStatus,
} from "../../../../constants";
import { useTranslation } from "../../../../hooks";
import { fromEnv } from "../../../../helpers";

type WrapperProps = {
  justifyContent?: string;
};

const WrapperEl = styled.div<WrapperProps>`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  width: 100%;
  padding: 1.125rem 1.5625rem;
  ${(props) =>
    props &&
    css`
      background: ${props.theme.colors.background.filePicker};
      border-radius: ${props.theme.dimension.spacing.xs}px;
      border: 1px dashed ${props.theme.colors.borders.filePicker};
    `}

  ${(props) =>
    props.justifyContent &&
    css`
      justify-content: ${props.justifyContent};
    `}
`;

const HelperTextEl = styled(Text)`
  color: rgba(31, 36, 42, 0.5);
  line-height: 0.875rem;
`;

const FooterEl = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 0.75rem;
`;

type FooterProps = {
  topDesc?: string;
  bottomDesc?: string;
  buttonText: string;
  onButtonClick: () => any;
  loading?: boolean;
};

export const Footer = ({
  topDesc,
  bottomDesc,
  buttonText,
  onButtonClick,
  loading,
}: FooterProps) => {
  return (
    <FooterEl>
      {topDesc && (
        <Text fontSize="small" color="placeholder">
          {topDesc}
        </Text>
      )}
      <Button onClick={onButtonClick} loading={!!loading}>
        {buttonText}
      </Button>
      {bottomDesc && (
        <HelperTextEl
          fontSize="xsmall"
          color="placeholder"
          textCenter
          fontWeight="demi"
        >
          {bottomDesc}
        </HelperTextEl>
      )}
    </FooterEl>
  );
};

export type LivenessProps = {
  verified: boolean;
  onVerified: () => void;
};

export const Liveness = observer(({ verified, onVerified }: LivenessProps) => {
  const translate = useTranslation();
  const [result, setResult] = useState<ResultData | null>(null);
  const [showResultModal, setShowResultModal] = useState<boolean>(false);
  const { validateAndIncreaseCount, count } = useOneKycAttemptCounter();
  const isMaxAttemptsReached = count >= 3;
  const isVerified = verified || result?.result === LivenessResult.Valid;

  const {
    title: resultTitle,
    image: resultImage,
    helperText: resultHelperText,
    buttonText: resultButtonText,
  } = useResultTranslation(result, count);

  const {
    showInitModal,
    showConfirmCloseModal,
    launchUrl,
    qrLaunchUrl,
    handleStart,
    handleCloseInitModal,
    handleCamera,
    handleQR,
    handleConfirmCloseModal,
    handleCloseConfirmModal,
    handleOnConfirmedClose,
    resetState,
  } = useInitOneKycProcess({
    setResult: (result: ResultData | null) => setResult(result),
    setShowResultModal: (show: boolean) => setShowResultModal(show),
  });

  const {
    loading,
    handleResult,
    handleFinishResult,
    handleCloseResult,
  } = useOneKycResult({
    resetState,
    setShowResultModal: (show: boolean) => setShowResultModal(show),
    onHandleResult: (result: ResultData) => setResult(result),
    onHandleCloseResult: () => {
      if (isVerified) {
        onVerified();
        return;
      }

      validateAndIncreaseCount(result);
    },
    onHandleFinishResult: () => {
      if (isVerified) {
        onVerified();
        return;
      }

      validateAndIncreaseCount(result);

      if (!isMaxAttemptsReached) {
        handleStart();
      }
    },
  });

  useEffect(() => {
    const restrictAttempts = () => {
      if (verified) return;

      if (isMaxAttemptsReached) {
        setResult({
          status: LIvenessStatus.Error,
          result: LivenessResult.Invalid,
          errorCode: LivenessErrorCode.MaxAttempts,
        });
      }
    };

    restrictAttempts();
  }, [isMaxAttemptsReached, verified]);

  useEffect(() => {
    if (verified) {
      setResult({
        status: LIvenessStatus.Completed,
        result: LivenessResult.Valid,
      });
    }
  }, [verified]);

  if (verified) {
    return (
      <>
        <WrapperEl justifyContent="center">
          <img src={resultImage} alt="Liveness success" />
          <Text
            fontSize="normal"
            fontWeight="demi"
            marginTopS
            marginBottomS
            color="success"
            align="center"
          >
            {resultTitle}
          </Text>
        </WrapperEl>
        {result && (
          <ResultModal
            show={showResultModal}
            onClose={handleCloseResult}
            result={result}
            onBtnClick={handleFinishResult}
          />
        )}
      </>
    );
  }

  if (result?.status === LIvenessStatus.Error) {
    return (
      <>
        <WrapperEl>
          <img src={resultImage} alt="Liveness failure" />
          <Text
            fontSize="normal"
            fontWeight="demi"
            marginTopS
            marginBottomS
            color="danger"
            align="center"
          >
            {resultTitle}
          </Text>
          <Footer
            topDesc={resultHelperText}
            buttonText={resultButtonText}
            onButtonClick={() => {
              if (isMaxAttemptsReached) {
                const supportUrl = fromEnv(
                  "REACT_APP_LIVENESS_CONTACT_SUPPORT_URL"
                );
                window.open(supportUrl, "_blank");
                return;
              }

              handleStart();
            }}
          />
        </WrapperEl>
        {result && (
          <ResultModal
            show={showResultModal}
            onClose={handleCloseResult}
            result={result}
            onBtnClick={handleFinishResult}
          />
        )}
      </>
    );
  }

  return (
    <WrapperEl>
      <img
        src={PlaceholderImg}
        alt="placeholder"
        style={{ marginTop: "1.375rem", marginBottom: "1.375rem" }}
      />
      <Footer
        buttonText={translate("owner_info.liveness.cta.start")}
        onButtonClick={handleStart}
        bottomDesc={translate("owner_info.liveness.helper_text")}
        loading={loading}
      />
      <InitLivenessModal
        show={showInitModal}
        onClose={handleCloseInitModal}
        onUseCamera={handleCamera}
        onUseQR={handleQR}
      />
      {launchUrl && (
        <CameraIFrameModal
          show={!!launchUrl}
          onClose={handleConfirmCloseModal}
          src={launchUrl}
          onResult={handleResult}
        />
      )}
      {qrLaunchUrl && (
        <QRIFrameModal
          show={!!qrLaunchUrl}
          onClose={handleConfirmCloseModal}
          src={qrLaunchUrl}
          onUseCamera={handleCamera}
          onResult={handleResult}
        />
      )}
      <ConfirmCloseModal
        show={showConfirmCloseModal}
        onClose={handleCloseConfirmModal}
        onConfirm={handleOnConfirmedClose}
      />
      {result && (
        <ResultModal
          show={showResultModal}
          onClose={handleCloseResult}
          result={result}
          onBtnClick={handleFinishResult}
        />
      )}
    </WrapperEl>
  );
});
